import type { Team } from '@compass/client/types/team';
import { ChevronsUpDown, Loader2, Trash2, Undo2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AssigneeMemberDisplay } from '@/components/AssigneeMemberDisplay';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { useOrganizationUsers } from '@/hooks/useOrganizationUsers';
import { useOrganizationTeams, useUpdateOrganizationTeam } from '@/hooks/useTeams';
import { useUsersByIds } from '@/hooks/useUsers';
import { useUsersSkillsMap } from '@/hooks/useUserSkills';
import { getApiErrorMessage } from '@/utils/apiError';

import { ALREADY_ON_TEAM_HINT, usersAlreadyOnATeam } from './teamMembership';

const HAS_SKILLS_HINT = 'Remove skills first';

interface ManageTeamMembersDialogProps {
  organizationId: string;
  team: Team | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ManageTeamMembersDialog: React.FC<ManageTeamMembersDialogProps> = ({
  organizationId,
  team,
  open,
  onOpenChange,
}) => {
  const [membersToRemove, setMembersToRemove] = useState<string[]>([]);
  const [membersToAdd, setMembersToAdd] = useState<string[]>([]);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentMemberIds = team?.member_ids || [];
  const { usersMap, isLoading: isLoadingMembers } = useUsersByIds(currentMemberIds);
  const { data: orgUsersData, isLoading: isLoadingOrgUsers } = useOrganizationUsers(
    organizationId,
    { limit: 100 },
  );
  const updateMutation = useUpdateOrganizationTeam();
  const { teams: orgTeams } = useOrganizationTeams(organizationId);
  const { skillsMap: memberSkillsMap, isLoading: isLoadingSkills } = useUsersSkillsMap(
    organizationId,
    currentMemberIds,
  );

  const currentMemberSet = useMemo(() => new Set(currentMemberIds), [currentMemberIds]);

  const memberHasSkills = (userId: string) => (memberSkillsMap.get(userId)?.length ?? 0) > 0;

  const assignedElsewhere = useMemo(
    () => usersAlreadyOnATeam(orgTeams, team?.id),
    [orgTeams, team?.id],
  );

  // Org users not already in the team (and not already selected to add)
  const availableUsers = useMemo(() => {
    return (orgUsersData?.users || []).filter(
      u => !currentMemberSet.has(u.user_id || '') && !membersToAdd.includes(u.user_id || ''),
    );
  }, [orgUsersData, currentMemberSet, membersToAdd]);

  // Users selected to add (for badge display)
  const usersToAddDisplay = useMemo(() => {
    return (orgUsersData?.users || []).filter(u => membersToAdd.includes(u.user_id || ''));
  }, [orgUsersData, membersToAdd]);

  useEffect(() => {
    if (!open) {
      setMembersToRemove([]);
      setMembersToAdd([]);
      setIsSubmitting(false);
      setComboboxOpen(false);
    }
  }, [open]);

  const toggleAddUser = (userId: string) => {
    if (assignedElsewhere.has(userId)) return;
    setMembersToAdd(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId],
    );
  };

  const toggleRemoveMember = (userId: string) => {
    // A member with skills can't be removed from their team (backend invariant) —
    // skills must be cleared first via the Manage Skills dialog.
    if (memberHasSkills(userId) && !membersToRemove.includes(userId)) return;
    setMembersToRemove(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId],
    );
  };

  const handleSubmit = async () => {
    if (!team?.id || (membersToRemove.length === 0 && membersToAdd.length === 0)) return;

    setIsSubmitting(true);
    try {
      // Backend doesn't allow both add and remove in one request — make sequential calls
      if (membersToRemove.length > 0) {
        await updateMutation.mutateAsync({
          organization_id: organizationId,
          team_id: team.id,
          remove_member_ids: membersToRemove,
        });
      }

      if (membersToAdd.length > 0) {
        await updateMutation.mutateAsync({
          organization_id: organizationId,
          team_id: team.id,
          add_member_ids: membersToAdd,
        });
      }

      const parts: string[] = [];
      if (membersToAdd.length > 0) parts.push(`${membersToAdd.length} added`);
      if (membersToRemove.length > 0) parts.push(`${membersToRemove.length} removed`);
      toast.success(`Team members updated: ${parts.join(', ')}`);
      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update team members'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!team) return null;

  const isLoading = isLoadingMembers || isLoadingOrgUsers || isLoadingSkills;
  const hasChanges = membersToRemove.length > 0 || membersToAdd.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Members - {team.name}</DialogTitle>
          <DialogDescription>Add or remove members from this team.</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Add Members */}
            <div>
              <Label>Add Members</Label>
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboboxOpen}
                    className="mt-2 w-full justify-between"
                  >
                    {membersToAdd.length > 0
                      ? `${membersToAdd.length} ${membersToAdd.length === 1 ? 'user' : 'users'} selected`
                      : 'Select users to add...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[550px] p-0">
                  <Command>
                    <CommandInput placeholder="Search users..." />
                    <CommandEmpty>No users found.</CommandEmpty>
                    <CommandList
                      className="max-h-[200px] overflow-y-auto"
                      onWheel={e => e.stopPropagation()}
                    >
                      <CommandGroup>
                        {availableUsers.map(user => {
                          const isSelected = membersToAdd.includes(user.user_id || '');
                          const isDisabled = assignedElsewhere.has(user.user_id || '');
                          return (
                            <CommandItem
                              key={user.user_id}
                              value={`${user.name} ${user.email}`}
                              disabled={isDisabled}
                              onSelect={() => {
                                if (isDisabled) return;
                                toggleAddUser(user.user_id || '');
                              }}
                            >
                              <Checkbox checked={isSelected} className="mr-2" />
                              <AssigneeMemberDisplay
                                name={user.name || undefined}
                                email={user.email || ''}
                                userId={user.user_id}
                              />
                              {isDisabled && (
                                <span className="ml-auto text-xs text-muted-foreground">
                                  {ALREADY_ON_TEAM_HINT}
                                </span>
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {usersToAddDisplay.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {usersToAddDisplay.map(user => (
                    <Badge key={user.user_id} variant="secondary" className="gap-1">
                      {user.name || user.email}
                      <button
                        onClick={() => toggleAddUser(user.user_id || '')}
                        className="ml-1 rounded-full hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Current Members */}
            <div>
              <Label className="text-sm font-medium">
                Current Members ({currentMemberIds.length})
              </Label>
              {currentMemberIds.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">No members in this team yet.</p>
              ) : (
                <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                  {currentMemberIds.map(userId => {
                    const user = usersMap.get(userId);
                    const isMarkedForRemoval = membersToRemove.includes(userId);
                    const hasSkills = memberHasSkills(userId);
                    const removalDisabled = hasSkills && !isMarkedForRemoval;

                    return (
                      <div
                        key={userId}
                        className={`flex items-center justify-between rounded-md border px-3 py-2 ${
                          isMarkedForRemoval ? 'border-destructive/50 bg-destructive/5' : ''
                        }`}
                      >
                        <div className="flex flex-col gap-0.5">
                          <AssigneeMemberDisplay
                            name={user?.name || undefined}
                            email={user?.email || userId}
                            userId={userId}
                          />
                        </div>
                        <SimpleTooltipV2 content={removalDisabled ? HAS_SKILLS_HINT : ''}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRemoveMember(userId)}
                            disabled={removalDisabled}
                            className={
                              isMarkedForRemoval ? 'text-destructive hover:text-destructive' : ''
                            }
                          >
                            {isMarkedForRemoval ? (
                              <>
                                <Undo2 className="mr-1 h-4 w-4" />
                                Undo
                              </>
                            ) : (
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </SimpleTooltipV2>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!hasChanges || isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
